import React from 'react';
import { Star } from 'lucide-react';

/**
 * Star Rating Component
 * Displays 5 clickable stars for rating contacts
 *
 * @param {number} rating - Current rating (0-5)
 * @param {Function} onRatingChange - Callback when rating changes (receives new rating)
 * @param {string} size - Size of stars: 'sm' | 'md' | 'lg' (default: 'md')
 * @param {boolean} readonly - If true, stars are not clickable (default: false)
 * @param {boolean} showEmpty - If true, shows empty stars even at 0 rating (default: true)
 */
const StarRating = ({
    rating = 0,
    onRatingChange,
    size = 'md',
    readonly = false,
    showEmpty = true
}) => {
    const sizeClasses = {
        sm: 'w-3 h-3',
        md: 'w-4 h-4',
        lg: 'w-5 h-5'
    };

    const gapClasses = {
        sm: 'gap-0.5',
        md: 'gap-0.5',
        lg: 'gap-1'
    };

    const handleStarClick = (starIndex, e) => {
        e.stopPropagation();
        if (readonly || !onRatingChange) return;

        // If clicking the same star as current rating, remove rating
        // Otherwise set to clicked star
        const newRating = rating === starIndex ? 0 : starIndex;
        onRatingChange(newRating);
    };

    // Don't render anything if rating is 0 and showEmpty is false
    if (rating === 0 && !showEmpty) {
        return null;
    }

    return (
        <div className={`flex ${gapClasses[size]} items-center`}>
            {[1, 2, 3, 4, 5].map((star) => {
                const isFilled = star <= rating;

                return (
                    <button
                        key={star}
                        type="button"
                        onClick={(e) => handleStarClick(star, e)}
                        disabled={readonly}
                        className={`
                            ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}
                            transition-transform duration-150 focus:outline-none
                            ${!readonly && 'active:scale-95'}
                        `}
                        aria-label={`${star} star${star > 1 ? 's' : ''}`}
                    >
                        <Star
                            className={`
                                ${sizeClasses[size]}
                                transition-colors duration-150
                                ${isFilled
                                    ? 'text-yellow-400 fill-yellow-400'
                                    : 'text-gray-300 hover:text-yellow-300'
                                }
                            `}
                        />
                    </button>
                );
            })}
        </div>
    );
};

export default StarRating;
